export default {
  data() {
    return {
      initialStates: {}, // Stores initial states of watched objects
      hasChanges: {}, // Tracks changes per object
      nextRoute: null, // Stores the route the user is attempting to navigate to
      watchedObjectNames: new Set(),
    }
  },
  watch: {},
  methods: {
    /**
     * Initializes the watcher for multiple objects.
     * @param {Array} objects - Array of object names (as strings) to watch.
     */
    initializeChangewatcher(objects) {
      this.$nextTick(() => {
        objects.forEach((objectName) => {
          if (this[objectName] !== undefined) {
            this.watchedObjectNames.add(objectName)
            this.initialStates[objectName] = JSON.parse(
              JSON.stringify(this[objectName])
            )
            this.$set(this.hasChanges, objectName, false)
          } else {
            console.warn(
              `Object "${objectName}" is not defined in the component's data.`
            )
          }
        })
      })
    },

    /**
     * Normalizes values to treat null, undefined, and empty strings/arrays as equivalent.
     * @param {*} value - The value to normalize.
     * @returns {*} - Normalized value.
     */
    normalizeValue(value) {
      if (value === null || value === undefined) {
        return ''
      }
      if (Array.isArray(value) && value.length === 0) {
        return []
      }
      return value
    },

    /**
     * Checks if a value is "empty" (null, undefined, or empty string).
     * @param {*} value
     * @returns {Boolean}
     */
    isEmptyValue(value) {
      return value === null || value === undefined || value === ''
    },

    /**
     * Deeply compares two objects with normalization.
     * Uses the union of keys from both objects so that new properties
     * with null/undefined values don't trigger a false change.
     * @param {Object} obj1 - First object.
     * @param {Object} obj2 - Second object.
     * @returns {Boolean} - True if objects are equal, false otherwise.
     */
    areObjectsEqual(obj1, obj2) {
      if (obj1 === obj2) return true
      if (this.isEmptyValue(obj1) && this.isEmptyValue(obj2)) return true
      if (this.isEmptyValue(obj1) || this.isEmptyValue(obj2)) return false

      if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
        return this.normalizeValue(obj1) === this.normalizeValue(obj2)
      }

      // Use union of keys so added properties with empty values are ignored
      const allKeys = new Set([
        ...Object.keys(obj1),
        ...Object.keys(obj2),
      ])

      for (let key of allKeys) {
        const val1 = this.normalizeValue(obj1[key])
        const val2 = this.normalizeValue(obj2[key])

        // Both empty → equal, skip
        if (this.isEmptyValue(val1) && this.isEmptyValue(val2)) continue

        const areBothObjects =
          typeof val1 === 'object' &&
          val1 !== null &&
          typeof val2 === 'object' &&
          val2 !== null
        if (areBothObjects) {
          if (!this.areObjectsEqual(val1, val2)) return false
        } else if (val1 !== val2) {
          return false
        }
      }

      return true
    },

    /**
     * Checks if a specific object has unsaved changes.
     * @param {String} objectName - The name of the object to check.
     * @param {Object} currentObject - The current state of the object.
     */
    checkForUnsavedChanges(objectName, currentObject) {
      const initialObject = this.initialStates[objectName]
      const isEqual = this.areObjectsEqual(currentObject, initialObject)
      this.$set(this.hasChanges, objectName, !isEqual)
    },

    /**
     * Handles the discard action from the modal.
     */
    handleDiscard() {
      // Reset all watched objects to their initial states
      Object.keys(this.initialStates).forEach((objectName) => {
        this[objectName] = JSON.parse(
          JSON.stringify(this.initialStates[objectName])
        )
        this.$set(this.hasChanges, objectName, false)
      })
      this.navigateToNextRoute()
    },

    /**
     * Handles the stay action from the modal.
     */
    handleStay() {
      this.nextRoute = null
    },

    /**
     * Navigates to the stored next route after handling save/discard.
     */
    navigateToNextRoute() {
      if (this.nextRoute) {
        this.$router.push(this.nextRoute)
        this.nextRoute = null
      }
    },

    /**
     * Checks if any of the watched objects have unsaved changes.
     * @returns {Boolean} - True if there are unsaved changes, false otherwise.
     */
    hasAnyChanges() {
      return Object.values(this.hasChanges).some((change) => change)
    },

    commitChangewatcher(objects = Array.from(this.watchedObjectNames)) {
      objects.forEach((objectName) => {
        if (this[objectName] !== undefined) {
          this.initialStates[objectName] = JSON.parse(
            JSON.stringify(this[objectName])
          )
          this.$set(this.hasChanges, objectName, false)
        }
      })
    },
  },

  beforeRouteLeave(to, from, next) {
    Array.from(this.watchedObjectNames).forEach((objectName) => {
      this.checkForUnsavedChanges(objectName, this[objectName])
    })

    if (this.hasAnyChanges()) {
      this.$bus.$emit('open-modal', 'unsaved-changes-modal')
      this.nextRoute = to
      next(false) // <-- wichtig: Navigation wirklich abbrechen
    } else {
      next()
    }
  },
}
